import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { Sidebar } from '@/components/Sidebar';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { MainContent } from '@/components/MainContent';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useArtemisLauncher } from '@/hooks/useArtemisLauncher';
import { useLoanOfficerPermissionPreview } from '@/hooks/useLoanOfficerPermissionPreview';

// Legacy URL aliases — older notification emails may still link to these.
// We rewrite the URL to canonical form on mount so downstream consumers don't
// need to know about the legacy names.
const SECTION_ALIASES: Record<string, string> = {
  recruitment: 'recruiting',
  'company-profile-settings': 'company-profile',
  // Pipelines › Processing was retired 2026-10; old bookmarks open its sibling.
  processing: 'disclosures',
  pipelines: 'disclosures',
  // Workspace › Tasks (Admin Tasks) was retired 2026-10-12; old bookmarks open Recruiting.
  tasks: 'recruiting'
};
const TAB_ALIASES: Record<string, string> = {
  contract: 'contract-details'
};
// Retired sections → canonical successor route. Old bookmarks / notification
// emails may still link to ?section=<retired>; redirect them to the replacement
// feature instead of falling through to MainContent's "under development" page.
const RETIRED_SECTION_ROUTES: Record<string, string> = {
  opportunities: '/internal/artemis',
  'rate-quote': '/internal/artemis',
  'rate-quote-create': '/internal/artemis',
  'rate-quote-tool-template': '/internal/artemis'
};

const normalizeSection = (raw: string | null): string | null =>
  raw ? (SECTION_ALIASES[raw] ?? raw) : raw;

const InternalPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeItem, setActiveItem] = useState(() => {
    // Check if we have a saved path from before refresh
    const savedPath = sessionStorage.getItem('auth-redirect-path');
    if (savedPath) {
      sessionStorage.removeItem('auth-redirect-path');
      // Extract section from saved path
      const pathMatch = savedPath.match(/section=([^&]+)/);
      if (pathMatch) {
        return normalizeSection(pathMatch[1]) ?? pathMatch[1];
      }
      // Map common paths to sections
      const pathToSection: Record<string, string> = {
        '/internal/rate-quote': 'artemis',
        '/internal/pre-approval': 'pre-approval',
        '/internal/pipeline': 'pipeline',
        '/internal/contacts': 'borrowers',
        '/internal/settings': 'my-profile'
      };
      for (const [path, section] of Object.entries(pathToSection)) {
        if (savedPath.startsWith(path)) {
          return section;
        }
      }
    }
    // Get initial state from URL or default to dashboard
    return normalizeSection(searchParams.get('section')) || 'home';
  });
  const [isFixedMode, setIsFixedMode] = useState(true);
  const [isHomeCustomizing, setIsHomeCustomizing] = useState(false);
  const handleSetActiveItem = React.useCallback((sectionId: string) => {
    setActiveItem(normalizeSection(sectionId) ?? sectionId);
  }, []);
  const { user, signOut } = useAuth();
  const roleAccess = useRoleAccess();
  const navigate = useNavigate();
  const openArtemis = useArtemisLauncher({
    userKey: user?.email || 'anonymous'
  });
  const isEffectiveAdmin = !roleAccess.isLoadingPermissions
    && !roleAccess.isImpersonating
    && roleAccess.hasAnyPermission(['Admin']);
  const {
    isLoadingLoanOfficerPermissions,
    loanOfficerHasAnyPermission
  } = useLoanOfficerPermissionPreview(
    activeItem === 'home' && isHomeCustomizing && isEffectiveAdmin
  );

  // Profile sync no longer needed - using single users table

  // Redirect retired sections to their successor route (Opportunities → Artemis,
  // 2026-06). Covers old bookmarks and notification-email deep links so they no
  // longer fall through to MainContent's generic "under development" page.
  useEffect(() => {
    const section = searchParams.get('section');
    if (section && RETIRED_SECTION_ROUTES[section]) {
      navigate(RETIRED_SECTION_ROUTES[section], { replace: true });
    }
  }, [searchParams, navigate]);

  // Normalize legacy section/tab aliases in the URL (covers in-flight email
  // links that were sent before the URL builder was fixed). Rewrites to
  // canonical names so downstream consumers (Recruiting.tsx) only need to
  // handle the current schema.
  useEffect(() => {
    const rawSection = searchParams.get('section');
    const rawTab = searchParams.get('tab');
    const aliasedSection = rawSection && SECTION_ALIASES[rawSection];
    const aliasedTab = rawTab && TAB_ALIASES[rawTab];
    if (!aliasedSection && !aliasedTab) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    if (aliasedSection) {
      next.set('section', aliasedSection);
    }
    if (aliasedTab) {
      next.set('tab', aliasedTab);
      // Legacy `tab=contract` was emitted only by recruiter_lo_signed and
      // always meant "open the review modal" — preserve the semantic when
      // the new action signal is absent.
      if (rawTab === 'contract' && !searchParams.get('action')) {
        next.set('action', 'review-contract');
      }
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync activeItem with URL changes (when navigating programmatically)
  useEffect(() => {
    const section = normalizeSection(searchParams.get('section'));
    if (section && section !== activeItem) {
      setActiveItem(section);
    } else if (!section && activeItem !== 'home') {
      setActiveItem('home');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activeItem read is intentional; adding it would re-fire after the setter
  }, [searchParams]);

  // Update URL when activeItem changes (when clicking sidebar)
  useEffect(() => {
    // The redirect effect owns retired sections. Do not write one back into
    // the query string while its successor route is replacing the location.
    if (RETIRED_SECTION_ROUTES[activeItem]) {
      return;
    }

    const currentSection = searchParams.get('section');
    if (activeItem === 'home') {
      // Remove section param for dashboard (clean URL)
      if (currentSection) {
        searchParams.delete('section');
        setSearchParams(searchParams, { replace: true });
      }
    } else if (currentSection !== activeItem) {
      searchParams.set('section', activeItem);
      setSearchParams(searchParams, { replace: true });
    }
  }, [activeItem, searchParams, setSearchParams]);

  // Customize is a Home-only, self-owned preference. Navigating away or
  // entering impersonation always returns the shell to its ordinary state so
  // the authenticated administrator's preference row cannot be mutated while
  // previewing somebody else.
  useEffect(() => {
    if (activeItem !== 'home' || roleAccess.isImpersonating) {
      setIsHomeCustomizing(false);
    }
  }, [activeItem, roleAccess.isImpersonating]);

  return (
    <SidebarProvider
      defaultOpen={isFixedMode}
      open={isFixedMode}
      onOpenChange={setIsFixedMode}
    >
      <div className="flex h-screen w-full bg-background overflow-hidden">
        <Sidebar
          user={user!}
          signOut={signOut}
          roleAccess={roleAccess}
          activeItem={activeItem}
          onSetActiveItem={handleSetActiveItem}
          onOpenArtemis={openArtemis}
          isFixedMode={isFixedMode}
          onFixedModeChange={setIsFixedMode}
          isHomeCustomizing={isHomeCustomizing}
          isEffectiveAdmin={isEffectiveAdmin}
          loanOfficerHasAnyPermission={loanOfficerHasAnyPermission}
          isLoadingLoanOfficerPermissions={isLoadingLoanOfficerPermissions}
        />

        <main className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
          <div className="lw-ds lw-shell-mobile-header flex h-14 shrink-0 items-center gap-3 px-3 md:hidden">
            <SidebarTrigger className="h-10 w-10" aria-label="Open portal navigation" />
            <div className="min-w-0">
              <span className="block font-serif text-lg font-medium leading-none text-[var(--green-800)]">LendWise</span>
              <span className="mt-1 block text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--warn-ink)]">
                Mortgage Platform
              </span>
            </div>
          </div>
          <MainContent
            activeItem={activeItem}
            roleAccess={roleAccess}
            setActiveItem={handleSetActiveItem}
            onOpenArtemis={openArtemis}
            isCustomizing={isHomeCustomizing}
            onCustomizingChange={setIsHomeCustomizing}
            isEffectiveAdmin={isEffectiveAdmin}
            loanOfficerHasAnyPermission={loanOfficerHasAnyPermission}
            isLoadingLoanOfficerPermissions={isLoadingLoanOfficerPermissions}
            canCustomizeHome={!roleAccess.isImpersonating}
          />
        </main>
      </div>
    </SidebarProvider>
  );
};

export default InternalPage;
